import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from './tooltip';
import FanQrScanner, { requestFanCamera } from './FanQrScanner';

export const normaliseFan = (value) => value.replace(/\D/g, '').slice(0, 16);

export const formatFan = (value) => value.match(/.{1,4}/g)?.join(' ') ?? '';

const mobileAutofill = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent ?? '';
  if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
};

const fanInputClass =
  'rounded-md appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-white focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 focus:z-10 sm:text-sm';

const fanInputCustomClass =
  'h-10 border border-input border-gray bg-transparent px-3 py-2 text-sm ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus:outline-none focus-visible:outline-none focus:border-gray focus-visible:border-gray file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-white/80 disabled:cursor-not-allowed disabled:bg-muted-light-gray shadow-none text-center background-transparent active:border-gray text-white';

const QrCodeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect width="5" height="5" x="3" y="3" rx="1" />
    <rect width="5" height="5" x="16" y="3" rx="1" />
    <rect width="5" height="5" x="3" y="16" rx="1" />
    <path d="M21 16h-3a2 2 0 0 0-2 2v3" />
    <path d="M21 21v.01" />
    <path d="M12 7v3a2 2 0 0 1-2 2H7" />
    <path d="M3 12h.01" />
    <path d="M12 3h.01" />
    <path d="M12 16v.01" />
    <path d="M16 12h1" />
    <path d="M21 12v.01" />
    <path d="M12 21v-1" />
  </svg>
);

/**
 * Shared FAN/FCN field used by OTP and Biometrics identify steps.
 * idPrefix should be "Otp" | "Bio" so verifayda-ui-fix can polish the DOM.
 */
export default function FanIdField({
  idPrefix = 'Otp',
  loginId,
  individualId,
  onChange,
  onBlur,
  openIDConnectService,
  i18nKeyPrefix = 'otp',
}) {
  const { t } = useTranslation('translation', { keyPrefix: i18nKeyPrefix });
  const fanInputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const cameraRequestRef = useRef(null);
  const [showFanQrScanner, setShowFanQrScanner] = useState(false);
  const [cameraRequest, setCameraRequest] = useState(null);
  const fieldId = loginId ? `${idPrefix}_${loginId.id}` : '';
  onChangeRef.current = onChange;

  useEffect(() => {
    const input = fanInputRef.current;
    if (!input) return undefined;

    const syncAutofill = (event) => {
      if (event?.animationName && event.animationName !== 'vf-autofill-start') {
        return;
      }
      onChangeRef.current({ target: input });
    };

    input.addEventListener('animationstart', syncAutofill, true);
    input.addEventListener('change', syncAutofill);
    input.addEventListener('input', syncAutofill);
    return () => {
      input.removeEventListener('animationstart', syncAutofill, true);
      input.removeEventListener('change', syncAutofill);
      input.removeEventListener('input', syncAutofill);
    };
  }, [fieldId]);

  if (!loginId) return null;

  const writeFanIntoField = (digits) => {
    const formatted = formatFan(digits);
    const input = fanInputRef.current;
    if (input && input.value !== formatted) {
      const setter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      )?.set;
      if (setter) {
        setter.call(input, formatted);
      } else {
        input.value = formatted;
      }
    }
    onChangeRef.current({
      target: {
        name: fieldId,
        value: digits,
        setCustomValidity: () => {},
      },
    });
  };

  const releaseCameraRequest = (request) => {
    if (!request || typeof request.then !== 'function') return;
    request
      .then((stream) => {
        stream.getTracks().forEach((track) => track.stop());
      })
      .catch(() => {});
  };

  const openFanScanner = () => {
    const request = requestFanCamera();
    cameraRequestRef.current = request;
    setCameraRequest(request);
    setShowFanQrScanner(true);
  };

  const closeFanScanner = () => {
    const request = cameraRequestRef.current;
    cameraRequestRef.current = null;
    setShowFanQrScanner(false);
    setCameraRequest(null);
    releaseCameraRequest(request);
  };

  const handleFanScanned = (fan) => {
    const digits = normaliseFan(fan);
    if (digits.length !== 16) {
      return;
    }
    writeFanIntoField(digits);
    closeFanScanner();
    requestAnimationFrame(() => writeFanIntoField(digits));
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <label htmlFor={fieldId} className="sr-only">
          {t('vid', { defaultValue: 'FAN/FCN' })}
        </label>
        <input
          ref={fanInputRef}
          onChange={onChange}
          onBlur={onBlur}
          value={formatFan(individualId ?? '')}
          id={fieldId}
          name={fieldId}
          type="tel"
          inputMode="numeric"
          autoComplete={mobileAutofill() ? 'username' : 'on'}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="done"
          pattern="[0-9]{16}"
          maxLength={40}
          onPaste={(event) => {
            const digits = normaliseFan(
              event.clipboardData?.getData('text') ?? ''
            );
            if (!digits) return;
            event.preventDefault();
            writeFanIntoField(digits);
          }}
          required
          className={`${fanInputClass} ${fanInputCustomClass} flex-1 min-w-0`}
          placeholder="1234 1234 1234 1234"
          title="Enter a 16 digit FAN"
        />
        <button
          type="button"
          onClick={openFanScanner}
          aria-label={t('scan_fan_qr', { defaultValue: 'Scan QR' })}
          title={t('scan_fan_qr', { defaultValue: 'Scan QR' })}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-gray bg-transparent text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        >
          <QrCodeIcon />
        </button>
      </div>

      <div className="flex justify-center items-center text-white mt-3 gap-2 flex-wrap">
        <Tooltip
          content={
            <p className="text-center whitespace-normal max-w-xs">
              {t('what_is_fan_answer', {
                defaultValue:
                  'Your 16-digit number found on your Fayda digital ID card',
              })}
            </p>
          }
        >
          <button
            type="button"
            className="px-2 py-1 flex items-center gap-1.5 text-sm"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
            </svg>
            {t('what_is_fan', { defaultValue: 'FAN/FCN' })}
          </button>
        </Tooltip>
      </div>

      <FanQrScanner
        open={showFanQrScanner}
        cameraRequest={cameraRequest}
        onClose={closeFanScanner}
        onScan={handleFanScanned}
        openIDConnectService={openIDConnectService}
      />
    </>
  );
}
